import { useCallback } from 'react'
import { toast } from 'react-toastify'
import {
  fecthTwitterAuthUrl,
  fecthTwitterLoginAuthUrl,
  fecthTwitterBind,
  fecthTwitterUnBind,
  twitterLogin,
} from '@src/store/slices/account'
import { getReferralCode } from '@src/utils/jwt'
import { AppDispatch, RootState } from '@src/store/store'
import { useSelector, useDispatch } from 'react-redux'
import { AccountInfo, TwitterBind, TwitterLogin } from '@src/types/account'

export interface TwitterConnectParams {
  loginSuccCallback?: (data: AccountInfo) => void
  bindSuccCallback?: () => void
  unbindSuccCallback?: () => void
  type?: 'bind' | 'login'
}

export const useTwitterConnect = (twitterConnectParams?: TwitterConnectParams) => {
  const dispatch = useDispatch<AppDispatch>()
  const agentInfo = useSelector((state: RootState) => state.agent.agentInfo)
  const getConnectTwitterData = useCallback(() => {
    return new Promise((resolve) => {
      try {
        dispatch(
          twitterConnectParams?.type === 'login'
            ? fecthTwitterLoginAuthUrl()
            : fecthTwitterAuthUrl(),
        )
          .unwrap()
          .then(({ code, data }) => {
            if (code !== 0) {
              resolve(null)
              return
            }

            const oauthPopup = window.open(data.url, 'twitter-login', 'width=600,height=400')
            if (!oauthPopup) {
              toast.error('Please check your browser settings to allow pop-ups.', {
                autoClose: false,
              })
              resolve(null)
              return
            }
            const interval = setInterval(async () => {
              if (oauthPopup.closed) {
                clearInterval(interval)
                resolve(null)
                return
              }
              try {
                const url = new URL(oauthPopup.location.href)
                const oauthToken = url.searchParams.get('oauth_token')
                const oauthVerifier = url.searchParams.get('oauth_verifier')
                if (oauthToken && oauthVerifier) {
                  clearInterval(interval)
                  oauthPopup.close()
                  resolve({
                    oauthToken,
                    oauthVerifier,
                    agentId: agentInfo?.id,
                  })
                }
              } catch (error) {
                console.warn('Error retrieving OAuth parameters:', error)
              }
            }, 1000)
          })
          .catch((error) => {
            console.warn('Twitter login failed:', error)
            resolve(null)
          })
      } catch (error) {
        console.warn('Twitter login failed:', error)
        resolve(null)
      }
    })
  }, [dispatch, agentInfo, twitterConnectParams?.type])

  const handleConnect = useCallback(
    async (params: TwitterBind) => {
      const { code } = await dispatch(fecthTwitterBind(params)).unwrap()

      if (code !== 0) {
        console.warn('Login failed:', code)
        if (code === 200017) {
          toast.error('The twitter already bound')
        }
        return
      }

      twitterConnectParams?.bindSuccCallback?.()
    },
    [dispatch, twitterConnectParams],
  )
  const handleTwitterLogin = useCallback(async () => {
    const { oauthToken, oauthVerifier } = (await getConnectTwitterData()) as TwitterLogin
    const referralCode = getReferralCode() || ''
    const { code, data } = await dispatch(
      twitterLogin(
        referralCode ? { oauthToken, oauthVerifier, referralCode } : { oauthToken, oauthVerifier },
      ),
    ).unwrap()

    if (code !== 0) {
      console.warn('Login failed:', code)
      return
    }
    twitterConnectParams?.loginSuccCallback?.(data)
  }, [dispatch, twitterConnectParams])

  const handleConnectTwitter = useCallback(async () => {
    const data = await getConnectTwitterData()
    if (data) {
      await handleConnect(data as TwitterBind)
    } else {
      console.warn('Twitter connection data is undefined or invalid.')
    }
  }, [getConnectTwitterData, handleConnect])

  const handleDisconnectTwitter = useCallback(
    async (id: number) => {
      const { code } = await dispatch(fecthTwitterUnBind(id)).unwrap()
      if (code !== 0) {
        console.warn('Twitter unbinding failed:', code)
        return
      }
      twitterConnectParams?.unbindSuccCallback?.()
    },
    [dispatch, twitterConnectParams],
  )

  return { handleConnectTwitter, handleDisconnectTwitter, handleTwitterLogin }
}
